import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";

const artworks = [
  {
    id: "a2",
    title: "Winterhain",
    year: "Original · Verkäuflich",
    medium: "Fünfteilig · Acryl auf Pappe · je 10 × 20 cm",
    story:
      "Fünf schmale Tafeln, gemeinsam ein Wald im Winter. Karge Äste, letzte Blüten, verhaltenes Licht. Ein meditatives Werk über Vergänglichkeit und die Schönheit, die im Stillen entsteht.",
    url: "/winterhain.jpg",
    span: "md:col-span-12",
    ratio: "aspect-[3/1]",
    fitMode: "cover",
    forSale: true,
  },
  {
    id: "a1",
    title: "Kornblumenstille",
    year: "Original · Verkäuflich",
    medium: "Acryl auf Holz · 50 × 70 cm",
    story:
      "Ein üppiger Strauß tiefblauer Kornblumen vor rot-weißem Grund. Ein Bild voller Kontraste, laute Farbe, stille Blüte. Eine Erinnerung daran, dass Fülle und Zartheit nebeneinander wohnen dürfen.",
    url: "/kornblumen.jpg",
    span: "md:col-span-4",
    ratio: "aspect-[4/5]",
    fitMode: "cover",
    forSale: true,
  },
  {
    id: "a3",
    title: "Rückzug in Magenta",
    year: "Original · Verkäuflich",
    medium: "Diptychon · Acryl auf Pappe · je 10 × 20 cm",
    story:
      "Zwei Frauenrücken in leuchtendem Magenta. Kraft und Verletzlichkeit in derselben Bewegung. Ein Bild vom Einkehren, vom sanften Zu-sich-selbst-Kommen.",
    url: "/magenta.jpg",
    span: "md:col-span-4",
    ratio: "aspect-[4/5]",
    fitMode: "cover",
    forSale: true,
  },
  {
    id: "a4",
    title: "Aufbruch übers Meer",
    year: "Original · Verkäuflich",
    medium: "Acryl auf Leinwand · 100 × 120 cm",
    story:
      "Ein Boot mit goldenem Segel, ein wilder Himmel, ein aufgewühltes Meer. Ein Bild vom Vertrauen mitten in der Bewegung, vom Loslassen und Weiterziehen, wenn der Wind einen ruft.",
    url: "/aufbruch.jpg",
    span: "md:col-span-4",
    ratio: "aspect-[4/5]",
    fitMode: "cover",
    forSale: true,
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [active]);

  return (
    <section
      id="kunst"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-cream"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.28em] text-terracotta">
              Meine Kunst
            </span>
            <h2 className="mt-4 font-serif-elegant text-4xl sm:text-5xl md:text-6xl text-ocean leading-[1.05]">
              Bilder, die eine
              <br />
              <em className="not-italic text-terracotta">Geschichte flüstern.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-lg text-ink/75 font-light leading-relaxed">
            Jedes Werk trägt einen eigenen Moment in sich, eine Frage, ein
            Gefühl, ein leises Wissen. Wenn du magst, komm näher. Vielleicht
            begegnet dir hier etwas, das dich selbst berührt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
          {artworks.map((art, i) => (
            <motion.button
              key={art.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              onClick={() => setActive(art)}
              data-testid={`gallery-item-${art.id}`}
              className={`group relative overflow-hidden rounded-[1.5rem] ${art.span} ${art.ratio} bg-cream-alt text-left`}
            >
              <img
                src={art.url}
                alt={art.title}
                className={
                  art.fitMode === "cover" || art.fitMode === "cover-zoom"
                    ? "w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    : "w-full h-full object-contain p-2 md:p-3 transition-transform duration-[900ms] group-hover:scale-105"
                }
                loading="lazy"
              />
              {art.forSale && (
                <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-terracotta text-cream text-[10px] uppercase tracking-[0.2em] shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-cream" />
                  Zum Verkauf
                </span>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-ocean/85 via-ocean/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <div className="text-xs uppercase tracking-widest text-cream/80">
                  {art.year}
                </div>
                <div className="font-serif-elegant text-2xl md:text-3xl text-cream flex items-center gap-2">
                  {art.title}
                  <ArrowUpRight size={18} className="opacity-80" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <p className="mt-14 text-center text-ink/60 italic font-serif-elegant text-xl">
          Alle gezeigten Werke sind Originale und aktuell{" "}
          <span className="text-terracotta not-italic font-medium">zum Verkauf</span>.{" "}
          <a href="#kontakt" className="text-terracotta underline underline-offset-4">
            gerne unverbindlich anfragen
          </a>
          .
        </p>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={() => setActive(null)}
            data-testid="artwork-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35 }}
              className="relative bg-cream rounded-[1.75rem] md:rounded-[2rem] max-w-5xl w-full max-h-[92dvh] flex flex-col md:grid md:grid-cols-2 overflow-y-auto md:overflow-hidden overscroll-contain"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="artwork-modal-close"
                className="fixed md:absolute top-6 md:top-4 right-6 md:right-4 z-20 w-10 h-10 rounded-full bg-cream shadow-md flex items-center justify-center text-ocean hover:bg-terracotta hover:text-cream transition-colors"
                aria-label="Schließen"
              >
                <X size={18} />
              </button>
              <div className="aspect-[4/3] md:aspect-auto md:h-full bg-cream-alt flex items-center justify-center p-4 shrink-0">
                <img src={active.url} alt={active.title} className="max-w-full max-h-full object-contain" />
              </div>
              <div className="p-6 md:p-10 flex flex-col md:justify-center md:overflow-y-auto">
                <div className="text-xs uppercase tracking-[0.24em] text-terracotta">
                  {active.year}
                </div>
                <h3 className="mt-3 font-serif-elegant text-3xl md:text-5xl text-ocean leading-tight md:leading-none">
                  {active.title}
                </h3>
                <div className="mt-3 text-sm text-ink/60">{active.medium}</div>
                <p className="mt-5 md:mt-6 text-ink/80 leading-relaxed font-light text-base md:text-lg">
                  {active.story}
                </p>
                <a
                  href="#kontakt"
                  onClick={() => setActive(null)}
                  data-testid="artwork-modal-cta"
                  className="mt-7 md:mt-8 inline-flex w-max items-center gap-2 px-6 py-3 rounded-full bg-terracotta text-cream text-sm hover:bg-terracotta-hover transition-colors"
                >
                  Werk anfragen <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
